import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, ExternalLink, ChevronDown, ChevronUp, Utensils, Coffee, UtensilsCrossed, RotateCcw, Minus } from 'lucide-react'
import { api } from '@/lib/api'
import { MacroBadgeRow } from './MacroBar'
import DishPicker from './DishPicker'
import type { MealSlot, Dish, SlotType, SlotItem } from '@/types/models'
import { getMealLabel } from '@/lib/utils'

interface Props {
  slot: MealSlot
  planId: string
  resolvedGroupSize: number
  calorie_target: number | null
}

const SLOT_TYPE_ICONS: Record<SlotType, React.ReactNode> = {
  meal: <Utensils className="w-3.5 h-3.5" />,
  snack: <UtensilsCrossed className="w-3.5 h-3.5" />,
  beverage: <Coffee className="w-3.5 h-3.5" />,
}

const SKIP_REASON_LABELS: Record<string, string> = {
  eating_out: 'Eating out',
  not_hungry: 'Not hungry',
  leftovers: 'Leftovers',
  fasting: 'Skipping',
}

function getEffectiveItems(slot: MealSlot): SlotItem[] {
  if (slot.items && slot.items.length > 0) return slot.items
  if (slot.dish_name) return [{ dish_id: slot.dish_id, dish_name: slot.dish_name, servings: slot.servings }]
  return []
}

export default function MealSlotCard({ slot, planId, resolvedGroupSize, calorie_target }: Props) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [pendingItems, setPendingItems] = useState<SlotItem[] | null>(null)
  const [updateError, setUpdateError] = useState<string | null>(null)
  const [editingServings, setEditingServings] = useState<{ index: number; value: string } | null>(null)

  const label = getMealLabel(slot.slot_index, slot.slot_type, slot.label)

  const updateSlot = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      return api.patch(`/slots/${slot.id}`, body, token)
    },
    onMutate: (body) => {
      setUpdateError(null)
      if ('items' in body) setPendingItems(body.items as SlotItem[])
    },
    onSettled: () => {
      setPendingItems(null)
      queryClient.invalidateQueries({ queryKey: ['plan', planId] })
    },
    onError: (err: Error) => {
      setUpdateError(err.message ?? 'Failed to update. Please try again.')
    },
  })

  const currentItems: SlotItem[] = pendingItems ?? getEffectiveItems(slot)
  const isLoading = updateSlot.isPending
  const hasItems = currentItems.length > 0

  function handleSelectDish(dish: Dish) {
    setPickerOpen(false)
    const newItems: SlotItem[] = [...currentItems, { dish_id: dish.id, dish_name: dish.name, servings: 1 }]
    updateSlot.mutate({ items: newItems, is_skipped: false, skip_reason: null })
  }

  function handleSkip(reason: string) {
    setPickerOpen(false)
    updateSlot.mutate({ is_skipped: true, skip_reason: reason, items: [] })
  }

  function handleClear() {
    updateSlot.mutate({ is_skipped: false, skip_reason: null, items: [] })
  }

  function handleUpdateServings(index: number, newServings: number) {
    if (newServings < 0.5) return
    const newItems = currentItems.map((item, i) =>
      i === index ? { ...item, servings: newServings } : item
    )
    updateSlot.mutate({ items: newItems })
  }

  function handleRemoveItem(index: number) {
    const newItems = currentItems.filter((_, i) => i !== index)
    updateSlot.mutate({ items: newItems, is_skipped: false })
  }

  function commitServingsEdit() {
    if (!editingServings) return
    const parsed = parseFloat(editingServings.value)
    if (!isNaN(parsed) && parsed >= 0.5) {
      handleUpdateServings(editingServings.index, parsed)
    }
    setEditingServings(null)
  }

  const primaryItem = currentItems[0] ?? null

  return (
    <>
      <div className={`rounded-xl border transition-all ${
        slot.is_skipped
          ? 'border-gray-100 bg-gray-50'
          : hasItems
          ? 'border-brand-200 bg-white shadow-sm'
          : 'border-dashed border-gray-300 bg-white hover:border-brand-300 hover:bg-brand-50/30'
      }`}>
        <div className="p-3">
          {/* Slot header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
              <span className="text-gray-400">{SLOT_TYPE_ICONS[slot.slot_type]}</span>
              {label}
              {resolvedGroupSize > 1 && slot.group_size && (
                <span className="text-gray-300 ml-1">· {slot.group_size}p</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {(hasItems || slot.is_skipped) && !isLoading && (
                <button onClick={handleClear} className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600">
                  <RotateCcw className="w-3 h-3" />
                </button>
              )}
              {hasItems && (
                <button onClick={() => setExpanded(e => !e)} className="p-1 hover:bg-gray-100 rounded text-gray-400">
                  {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              )}
            </div>
          </div>

          {/* Slot body */}
          {isLoading ? (
            <div className="space-y-1.5">
              {currentItems.length > 0
                ? currentItems.map((_, i) => <div key={i} className="h-7 bg-gray-100 rounded animate-pulse" />)
                : <div className="h-7 bg-gray-100 rounded animate-pulse" />
              }
            </div>
          ) : slot.is_skipped ? (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400 italic">
                {SKIP_REASON_LABELS[slot.skip_reason ?? ''] ?? 'Skipped'}
              </span>
              <button onClick={() => setPickerOpen(true)} className="text-xs text-brand-600 hover:underline">
                Change
              </button>
            </div>
          ) : hasItems ? (
            <div>
              {/* Item list */}
              <div className="space-y-1">
                {currentItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-1 group">
                    <button
                      onClick={() => setPickerOpen(true)}
                      className="flex-1 text-sm font-medium text-gray-900 hover:text-brand-700 text-left truncate min-w-0"
                    >
                      {item.dish_name}
                    </button>
                    {/* Servings stepper */}
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button
                        onClick={() => handleUpdateServings(index, Math.max(0.5, Math.round((item.servings - 0.5) * 2) / 2))}
                        className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 text-gray-400"
                      >
                        <Minus className="w-2.5 h-2.5" />
                      </button>
                      {editingServings?.index === index ? (
                        <input
                          type="number"
                          min={0.5}
                          step={0.5}
                          value={editingServings.value}
                          onChange={e => setEditingServings({ index, value: e.target.value })}
                          onBlur={commitServingsEdit}
                          onKeyDown={e => {
                            if (e.key === 'Enter') commitServingsEdit()
                            if (e.key === 'Escape') setEditingServings(null)
                          }}
                          className="w-12 text-center text-xs border border-brand-400 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-brand-500"
                          autoFocus
                        />
                      ) : (
                        <button
                          onClick={() => setEditingServings({ index, value: String(item.servings) })}
                          className="w-12 text-center text-xs text-gray-600 hover:text-brand-700 tabular-nums"
                        >
                          {item.servings % 1 === 0 ? item.servings : item.servings.toFixed(1)} srv
                        </button>
                      )}
                      <button
                        onClick={() => handleUpdateServings(index, Math.round((item.servings + 0.5) * 2) / 2)}
                        className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 text-gray-400"
                      >
                        <Plus className="w-2.5 h-2.5" />
                      </button>
                    </div>
                    {/* Remove button */}
                    <button
                      onClick={() => handleRemoveItem(index)}
                      className="w-5 h-5 flex items-center justify-center rounded hover:bg-red-50 text-gray-300 hover:text-red-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Aggregate macros */}
              <div className="mt-1.5">
                <MacroBadgeRow
                  calories={slot.total_calories}
                  protein_g={slot.total_protein_g}
                  carbs_g={slot.total_carbs_g}
                  fat_g={slot.total_fat_g}
                  compact
                />
              </div>

              {/* Add another */}
              <button
                onClick={() => setPickerOpen(true)}
                className="mt-1.5 flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700"
              >
                <Plus className="w-3 h-3" /> Add another
              </button>
            </div>
          ) : (
            <button onClick={() => setPickerOpen(true)}
              className="w-full flex items-center gap-2 py-1 text-sm text-gray-400 hover:text-brand-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add {slot.slot_type}
            </button>
          )}

          {/* Error state */}
          {updateError && (
            <p className="mt-1.5 text-xs text-red-500">{updateError}</p>
          )}

          {/* Expanded: add-ons + recipe links */}
          {expanded && hasItems && (
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
              {slot.add_ons?.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 mb-1">Add-ons</p>
                  {slot.add_ons.map((ao, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-gray-600">
                      <span>{ao.name}</span>
                      <span className="text-gray-400">{ao.calories} kcal</span>
                    </div>
                  ))}
                </div>
              )}
              {primaryItem && <RecipeLinks dishName={primaryItem.dish_name} />}
            </div>
          )}
        </div>
      </div>

      {pickerOpen && (
        <DishPicker
          slotType={slot.slot_type}
          slotLabel={label}
          onSelect={handleSelectDish}
          onSkip={handleSkip}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </>
  )
}

// Inline recipe link fetcher
function RecipeLinks({ dishName }: { dishName: string }) {
  const { getToken } = useAuth()
  const [show, setShow] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['recipes', dishName],
    queryFn: async () => {
      const token = await getToken()
      return api.get<{ data: { title: string; url: string; source: string }[] }>(
        `/ai/recipes?dish=${encodeURIComponent(dishName)}`, token!
      )
    },
    enabled: show,
  })

  const links = (data as { data: { title: string; url: string; source: string }[] } | undefined)?.data ?? []

  if (!show) {
    return (
      <button onClick={() => setShow(true)} className="text-xs text-brand-600 hover:underline">
        Find recipes →
      </button>
    )
  }

  return (
    <div>
      <p className="text-xs text-gray-400 mb-1.5">Recipes</p>
      {isLoading && <div className="h-4 bg-gray-100 rounded animate-pulse" />}
      {links.map((r, i) => (
        <a key={i} href={r.url} target="_blank" rel="noreferrer"
          className="flex items-center gap-1 text-xs text-brand-600 hover:underline py-0.5"
        >
          <ExternalLink className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{r.title}</span>
          <span className="text-gray-400 shrink-0">· {r.source}</span>
        </a>
      ))}
      {!isLoading && links.length === 0 && (
        <p className="text-xs text-gray-400">No recipes found.</p>
      )}
    </div>
  )
}

// Component for adding an extra slot (snack/beverage)
interface AddSlotProps {
  dayId: string
  planId: string
  nextIndex: number
  groupSize: number
}

export function AddSlotButton({ dayId, planId, nextIndex, groupSize }: AddSlotProps) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)

  const addSlot = useMutation({
    mutationFn: async (slotType: SlotType) => {
      const token = await getToken()
      return api.post(`/days/${dayId}/slots`, {
        slot_index: nextIndex,
        slot_type: slotType,
        group_size: groupSize,
        servings: 1,
      }, token!)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', planId] })
      setOpen(false)
    },
  })

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg border border-dashed border-gray-200 hover:border-brand-300 transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> Add snack or beverage
      </button>
    )
  }

  return (
    <div className="flex gap-2">
      {(['snack', 'beverage'] as const).map((type) => (
        <button key={type} onClick={() => addSlot.mutate(type)}
          disabled={addSlot.isPending}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 rounded-lg border border-brand-200 transition-colors capitalize"
        >
          {type === 'snack' ? <UtensilsCrossed className="w-3.5 h-3.5" /> : <Coffee className="w-3.5 h-3.5" />}
          {type}
        </button>
      ))}
      <button onClick={() => setOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}
